import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { omit } from "lodash";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Avatar,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

import { regexForName } from "common/constant";
import { addressOptions, salutationOptions } from "common/enums";
import {
  AuthorizedInfoWrapper,
  PlusIcon,
  ProfileImageWrapper,
  UploadImageButton,
} from "Components/AuthorizedPersonDetails/style";
import SelectField from "Components/Common/SelectField";
import InputFieldText from "Components/Common/InputFieldText";
import Modal from "Components/Common/Modal";
import Backdrop from "Components/Common/Backdrop";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";

import {
  filterEmptyStrings,
  generateFormData,
  getImageUrl,
  onlyNumberKeys,
} from "utils";
import getFileExtension from "utils/getFileExtension";
import debounceFn from "utils/debounce";

import {
  useLazyGetUserQuery,
  usePostConsumerDetailsMutation,
  usePutAuthorizedPersonDetailsMutation,
} from "apis/User";
import { useLazyLocationByPincodeQuery } from "apis/Location";
import { useLazyGetUtilityConsumerQuery } from "apis/UtilityConsumer/utilityConsumer";

const imageExt = ["jpg", "png", "jpeg"];

const validationSchema = {
  salutation: Yup.string().required("Please select your salutation").nullable(),
  firstName: Yup.string()
    .matches(regexForName, "Please enter valid first name")
    .required("Please enter your first name"),
  middleName: Yup.string().matches(
    regexForName,
    "Please enter valid middle name"
  ),
  lastName: Yup.string().matches(regexForName, "Please enter valid last name"),
  addressType: Yup.string().required("Please Select Address Type "),
  mobileNumber: Yup.string()
    .required("Mobile number is required")
    .matches(/^\d{10}$/, "Please enter valid mobile number"),
  addressLine1: Yup.string().required("Please enter your address"),
  city: Yup.object({
    id: Yup.number().required("City is required"),
  }),
  pinCode: Yup.string()
    .matches(/^\d{6}$/, "Please enter valid pincode")
    .required("Please enter your pincode"),
  email: Yup.string()
    .email("Please enter valid email")
    .required("Please enter your email"),
};

const initialQuery = {
  pageSize: 10,
  pageNumber: 1,
  consumerNumber: "",
};

const AddOrEditAuthorizedPerson = ({
  authorizedPersonUserId,
  handleClose,
  t,
}) => {
  const [
    getAuthorizedPersonDetails,
    {
      data: { data: authorizedPerson } = {},
      isLoading: isAuthorizedPersonDetailsLoading,
    },
  ] = useLazyGetUserQuery();

  const [
    getAllConsumerNumbers,
    {
      data: { data: { items: allConsumerNumbers } = [] } = {},
      isFetching: isConsumerNumbersFetching,
      isLoading: isConsumerNumbersLoading,
    },
  ] = useLazyGetUtilityConsumerQuery();

  const [updateAuthorizedPerson, { isLoading: isEditUserLoading }] =
    usePutAuthorizedPersonDetailsMutation();

  const [addAuthorizedUser, { isLoading: isAddUserLoading }] =
    usePostConsumerDetailsMutation();

  const [getLocation] = useLazyLocationByPincodeQuery();

  const [query, setQuery] = useState(initialQuery);
  const [pinCodeError, setPinCodeError] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [imgValidationError, setImgValidationError] = useState("");
  const [
    isConfirmationModalForChangeUserDetailOpen,
    setIsConfirmationModalForChangeUserDetailOpen,
  ] = useState(false);

  const updatedConsumerNumberOptions = useMemo(
    () =>
      allConsumerNumbers?.map((consumerNumber) => ({
        label: consumerNumber.consumerNumber,
        value: consumerNumber.id,
      })) ?? [],
    [allConsumerNumbers]
  );

  const initialValues = useMemo(
    () => ({
      id: authorizedPerson?.id ?? "",
      salutation: authorizedPerson?.salutation ?? "",
      firstName: authorizedPerson?.firstName ?? "",
      middleName: authorizedPerson?.middleName ?? "",
      lastName: authorizedPerson?.lastName ?? "",
      designation: authorizedPerson?.designation ?? "",
      email: authorizedPerson?.email ?? "",
      mobileNumber: authorizedPerson?.mobileNumber ?? "",
      addressType: authorizedPerson?.addressType ?? "",
      addressLine1: authorizedPerson?.addressLine1 ?? "",
      addressLine2: authorizedPerson?.addressLine2 ?? "",
      city: authorizedPerson?.city,
      pinCode: authorizedPerson?.pinCode ?? "",
      profileImageURL: getImageUrl(authorizedPerson?.profileImage) ?? "",
      file: "",
      consumerNumbers: [],
    }),
    [authorizedPerson]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      ...validationSchema,
      ...(!authorizedPersonUserId && {
        consumerNumbers: Yup.array().min(1, "Please select consumer number"),
      }),
    }),
    onSubmit: async (values) => {
      try {
        const payload = filterEmptyStrings({
          ...omit(values, ["city", "profileImageURL", "consumerNumbers"]),
          cityId: values.city?.id ?? "",
        });

        if (authorizedPersonUserId) {
          const formData = new FormData();
          generateFormData(formData, {
            ...payload,
            ...(!values.file && { profileImageURL: values.profileImageURL }),
          });
          await updateAuthorizedPerson(formData);
        } else {
          await addAuthorizedUser({
            ...payload,
            mobileNumber: values.mobileNumber.toString(),
            consumerNumbers: values.consumerNumbers.map(({ label }) => label),
          });
        }
        handleClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  const {
    setFieldValue,
    values: { profileImageURL, pinCode, consumerNumbers },
    handleBlur,
    handleSubmit,
    touched,
    errors,
  } = formik;

  const isValidFile = (file) => {
    const fileExtension = getFileExtension(file.name)?.toLowerCase();
    let errorMessage = "";

    if (imageExt.includes(fileExtension) && file.size > 600000) {
      errorMessage = "File size should be less than 600kB";
    } else if (!imageExt.includes(fileExtension)) {
      errorMessage =
        "Invalid file format, Please upload images in jpg, png, or jpeg format.";
    }

    setImgValidationError(errorMessage);
    if (errorMessage) {
      setFieldValue("file", "");
    }

    return !errorMessage;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && isValidFile(file)) {
      setFieldValue("file", file);
      setFieldValue("profileImageURL", URL.createObjectURL(file));
    }
    e.target.value = "";
  };

  const handleInputChange = (_, newInputValue) => {
    setInputValue(newInputValue);
    debounceFn(
      () =>
        getAllConsumerNumbers({
          ...query,
          consumerNumber: newInputValue,
        }),
      300
    );
  };

  const handleScroll = (event) => {
    const { target } = event;
    const { scrollTop, scrollHeight, clientHeight } = target;

    if (scrollTop + clientHeight >= scrollHeight) {
      setQuery((prev) => ({
        ...prev,
        pageSize: prev.pageSize + 10,
        consumerNumber: inputValue,
      }));
    }
  };

  useEffect(() => {
    if (!authorizedPersonUserId) {
      getAllConsumerNumbers(query);
    }
  }, [query]);

  useEffect(() => {
    if (authorizedPersonUserId) {
      getAuthorizedPersonDetails(authorizedPersonUserId);
    }
  }, [authorizedPersonUserId]);

  useEffect(() => {
    (async () => {
      if (pinCode.toString().length === 6) {
        try {
          const res = await getLocation(pinCode);
          setPinCodeError("");
          setFieldValue("city", res.data.city);
        } catch (error) {
          setPinCodeError(t("Please enter valid pincode"));
          setFieldValue("city", "");
        }
      }
    })();
  }, [pinCode, getLocation, setFieldValue]);

  if (isAuthorizedPersonDetailsLoading) {
    return <Backdrop open />;
  }

  return (
    <>
      <ConfirmationDialog
        isOpen={isConfirmationModalForChangeUserDetailOpen}
        title="Warning"
        message="Updating these user details will also affect the records for other utility consumers user details. Do you want to proceed?"
        onCancel={() => setIsConfirmationModalForChangeUserDetailOpen(false)}
        onConfirm={handleSubmit}
        isLoading={isEditUserLoading}
        variant="warning"
      />

      <Modal
        isOpen
        handleClose={handleClose}
        title={`${authorizedPersonUserId ? "Edit" : "Add"} User Details`}
        maxWidth="md"
      >
        {!!authorizedPersonUserId && (
          <ProfileImageWrapper>
            <UploadImageButton component="label">
              <Avatar src={profileImageURL} className="avatar" />
              <PlusIcon className="bg-white rounded-circle" />
              <input
                type="file"
                accept="file/*"
                onChange={handleImageChange}
                onBlur={handleBlur}
                hidden
              />
            </UploadImageButton>
            <Typography>
              {authorizedPerson?.profileImage || authorizedPerson?.file
                ? t("Profile Picture")
                : t("Upload Picture")}
            </Typography>
            {imgValidationError && (
              <Typography
                variant="subtitle2"
                className={"input-field-error-message"}
                color="error"
              >
                {imgValidationError}
              </Typography>
            )}
          </ProfileImageWrapper>
        )}

        <AuthorizedInfoWrapper>
          <Grid container spacing={2}>
            <Grid item sm={12}>
              <Grid container item columnSpacing={2}>
                <Grid item xs={12} sm={6} lg={4}>
                  <SelectField
                    label={t("Salutation")}
                    name="salutation"
                    formik={formik}
                    options={salutationOptions}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("First Name")}
                    name="firstName"
                    formik={formik}
                    placeholder="Enter First Name"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Middle Name")}
                    name="middleName"
                    formik={formik}
                    placeholder="Enter Middle Name"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Last Name")}
                    name="lastName"
                    formik={formik}
                    placeholder="Enter Last Name"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Designation")}
                    name="designation"
                    formik={formik}
                    placeholder="Enter Designation"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Email")}
                    name="email"
                    formik={formik}
                    type="email"
                    placeholder="Enter Email"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Mobile Number")}
                    name="mobileNumber"
                    formik={formik}
                    type="number"
                    InputProps={{
                      inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                    }}
                    placeholder="Enter Mobile Number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <SelectField
                    label={t("Address Type")}
                    name="addressType"
                    formik={formik}
                    options={addressOptions}
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Address Line 1")}
                    name="addressLine1"
                    formik={formik}
                    placeholder="Enter Address"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Address Line 2")}
                    name="addressLine2"
                    formik={formik}
                    placeholder="Enter Address"
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("City")}
                    name="city.name"
                    formik={formik}
                    placeholder="City"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <InputFieldText
                    label={t("Pincode")}
                    name="pinCode"
                    formik={formik}
                    errorMessage={pinCodeError}
                    type="number"
                    InputProps={{
                      inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                    }}
                    placeholder="Enter Pincode"
                  />
                </Grid>
                {!authorizedPersonUserId && (
                  <Grid item xs={12} sm={6} lg={4}>
                    <SelectField
                      label={t("Consumer Numbers")}
                      name="consumerNumbers"
                      formik={formik}
                      options={updatedConsumerNumberOptions}
                      onInputChange={handleInputChange}
                      multiple
                      onChange={(_, options, type) => {
                        const currentSelectedOption =
                          options[options.length - 1];
                        if (type === "removeOption") {
                          setFieldValue("consumerNumbers", options);
                        } else if (
                          consumerNumbers.some(
                            ({ value }) => value === currentSelectedOption.value
                          )
                        ) {
                          setFieldValue(
                            "consumerNumbers",
                            consumerNumbers.filter(
                              ({ value }) =>
                                value !== currentSelectedOption.value
                            )
                          );
                        } else {
                          setFieldValue("consumerNumbers", options);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("Consumer Numbers")}
                          error={
                            !!errors.consumerNumbers &&
                            !!touched.consumerNumbers
                          }
                          variant="outlined"
                          sx={{
                            position: "relative",
                          }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isConsumerNumbersLoading ||
                                isConsumerNumbersFetching ? (
                                  <CircularProgress
                                    size={25}
                                    sx={{
                                      position: "absolute",
                                      right: "10px",
                                    }}
                                  />
                                ) : (
                                  params.InputProps.endAdornment
                                )}
                              </>
                            ),
                          }}
                          type="text"
                        />
                      )}
                      disableCloseOnSelect
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option.value}>
                            <Checkbox
                              sx={{ marginRight: "8px" }}
                              checked={consumerNumbers.some(
                                ({ value }) => value === option.value
                              )}
                            />
                            {option.label}
                          </li>
                        );
                      }}
                      ListboxProps={{
                        onScroll: handleScroll,
                      }}
                      onClose={() => setQuery(initialQuery)}
                      value={consumerNumbers}
                      loading={
                        isConsumerNumbersLoading || isConsumerNumbersFetching
                      }
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </AuthorizedInfoWrapper>

        <Button
          className="float-end"
          variant="contained"
          onClick={() => {
            authorizedPerson?.isUserAssignedToOtherUtilityConsumers
              ? setIsConfirmationModalForChangeUserDetailOpen(true)
              : handleSubmit();
          }}
        >
          {(isEditUserLoading || isAddUserLoading) && (
            <CircularProgress size={20} />
          )}
          {t("Submit")}
        </Button>
      </Modal>
    </>
  );
};

AddOrEditAuthorizedPerson.propTypes = {
  authorizedPersonUserId: PropTypes.number,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(AddOrEditAuthorizedPerson);
